"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import type { ToolComponentData } from "@/lib/tool-adapter"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import {
  ANOTHER_BRAND,
  ANOTHER_BRAND_INSTRUCTION,
  MAKER_TERMS_LINKS,
  makerTermsLinkFor,
} from "@/lib/warranty/maker-links"

// FRIDGE-1250 — owner rule 09/18/2026: never restate another company's
// warranty as a Fridge.com claim. FRIDGE-1122, owner answer 09/24/2026:
// "Remove those promises. Explain coverage types and link to each
// manufacturer's exact terms." The per-brand term defaults are gone: the
// shopper enters each length from the warranty statement for their own model,
// and picking a brand shows a link to that maker's own warranty or support
// page (lib/warranty/maker-links.ts, shared with /warranty). The calculator
// only does date math — purchase date + the length the shopper entered.

/** Parse a length in years from the shopper's statement; blank or invalid is null. */
function parseYears(v: string): number | null {
  if (v.trim() === "") return null
  const n = Number(v)
  if (!Number.isFinite(n) || n <= 0 || n > 50) return null
  return n
}

/** "YYYY-MM-DD" from a date input as a LOCAL date (new Date(iso) is UTC and can show the day before). */
function parseLocalDate(v: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  if (m === null) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return isNaN(d.getTime()) ? null : d
}

const FALLBACK_RELATED = [
  "warranty-coverage-lookup",
  "refrigerator-warranty-comparison",
  "extended-warranty-break-even-calculator",
  "refrigerator-age-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  const candidates = [...slugs, ...FALLBACK_RELATED]
  for (const s of candidates) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const formatDate = (d: Date) =>
  d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })

type LayerResult =
  | { entered: false }
  | { entered: true; date: string; status: "Active" | "Expired"; months: number }

const LAYERS = [
  { key: "standard", label: "Parts + labor", hint: "Standard parts-and-labor term" },
  { key: "compressor", label: "Compressor", hint: "Only if your statement covers it longer" },
  { key: "sealed", label: "Sealed system", hint: "Only if your statement covers it longer" },
] as const

export function WarrantyExpirationCalculatorClient({ toolData }: { toolData?: ToolComponentData }) {
  const meta = toolData ?? TOOL_METADATA["warranty-expiration-calculator"]

  // Hooks first, THEN the early return (FRIDGE-307). The `meta` guard below
  // used to sit above these hooks: React requires every hook to be called
  // in the same order on every render, and a component that returns early on
  // one render and reaches the hooks on the next crashes with "Rendered more
  // hooks than during the previous render". None of these hooks reads `meta`,
  // so moving them up changes nothing about what renders.
  const [brand, setBrand] = useState<string>("")
  const [purchaseDate, setPurchaseDate] = useState<string>("")
  const [standardYears, setStandardYears] = useState<string>("")
  const [compressorYears, setCompressorYears] = useState<string>("")
  const [sealedYears, setSealedYears] = useState<string>("")

  const result = useMemo(() => {
    const purchase = parseLocalDate(purchaseDate)
    if (purchase === null) return null
    const lengths = {
      standard: parseYears(standardYears),
      compressor: parseYears(compressorYears),
      sealed: parseYears(sealedYears),
    }
    if (lengths.standard === null && lengths.compressor === null && lengths.sealed === null) return null
    const now = new Date()

    const layer = (years: number | null): LayerResult => {
      if (years === null) return { entered: false }
      const exp = new Date(purchase)
      exp.setMonth(exp.getMonth() + Math.round(years * 12))
      const active = exp > now
      const months = active
        ? Math.max(0, (exp.getFullYear() - now.getFullYear()) * 12 + (exp.getMonth() - now.getMonth()))
        : 0
      return { entered: true, date: formatDate(exp), status: active ? "Active" : "Expired", months }
    }

    return {
      standard: layer(lengths.standard),
      compressor: layer(lengths.compressor),
      sealed: layer(lengths.sealed),
    }
  }, [purchaseDate, standardYears, compressorYears, sealedYears])

  const maker = makerTermsLinkFor(brand)

  if (meta === undefined) return null

  const lengthInputs = [
    { key: "standard", label: "Parts + labor", value: standardYears, set: setStandardYears, placeholder: "e.g. 1" },
    { key: "compressor", label: "Compressor", value: compressorYears, set: setCompressorYears, placeholder: "Leave blank if not listed" },
    { key: "sealed", label: "Sealed system", value: sealedYears, set: setSealedYears, placeholder: "Leave blank if not listed" },
  ]

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="Is your refrigerator"
      heroTitleItalic="still"
      heroTitlePost="under warranty?"
      heroSubtitle="Enter your purchase date and the coverage lengths from your own warranty statement. We show exactly when each layer — parts and labor, compressor, sealed system — runs out, and which are still active."
      heroMeta={["⏱ ~1 min", "Lengths from your own warranty"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: String(LAYERS.length), label: "Coverage layers dated", note: "Labor · compressor · sealed system" },
            { value: String(MAKER_TERMS_LINKS.length), label: "Maker warranty pages linked", note: "Each maker's own site" },
            { value: "0", label: "Terms pre-filled", note: "You enter them from your statement" },
            { value: "Live", label: "Active or expired", note: "Per layer, as you type" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={[
        {
          question: "Why do I enter the warranty lengths myself?",
          answer:
            "Warranty terms are set by each maker and differ by model and model year. The statement for your exact model is the only reliable source, so the calculator uses the lengths you read from it rather than a pre-filled guess. Pick your brand to get a link to the maker's own warranty or support page.",
        },
        {
          question: "What date should I enter?",
          answer:
            "The documented purchase date — typically what's printed on your receipt or in the warranty registration confirmation. Your statement says whether coverage counts from purchase, delivery, or installation.",
        },
        {
          question: "What's the difference between the three coverage layers?",
          answer:
            "Standard parts and labor covers the whole unit, parts and the technician's labor both. Compressor coverage, where your statement lists it, extends to the compressor as a part. Sealed-system coverage extends to the whole refrigeration loop: compressor, evaporator, condenser, and connecting tubing. The longer layers are often parts-only, so check whether yours pays for labor.",
        },
        {
          question: "What happens after the standard term is up?",
          answer:
            "If a covered component fails inside a longer compressor or sealed-system window, your statement says what the maker pays for — often the part only, with the technician's labor billed to you. Anything outside those layers (ice maker, control board, fans, gaskets) is usually billed at full repair cost.",
        },
        {
          question: "Should I buy the extended warranty before the standard one expires?",
          answer:
            "Often yes, if you're buying it at all — most third-party plans only sell while the manufacturer's standard coverage is still active. Run the extended-warranty break-even calculator first; for many residential refrigerators, the math is closer than the upsell suggests.",
        },
      ]}
      relatedTools={buildRelated((meta as ToolComponentData)?.relatedTools ?? [], "warranty-expiration-calculator")}
      cta={{
        title: "Standard warranty expired and the fridge is past 10 years?",
        body: "Browse top-freezer refrigerators with strong reliability records and fresh manufacturer warranties.",
        buttonLabel: "Browse top-freezer refrigerators",
        buttonHref: "/top-freezer-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Purchase date.
                <br />
                Your warranty&apos;s lengths.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Date math against the terms in your own warranty statement. Nothing is pre-filled.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label
                    htmlFor="warranty-exp-brand"
                    className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2"
                  >
                    Refrigerator brand (for the maker&apos;s link)
                  </label>
                  <select
                    id="warranty-exp-brand"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="">Choose your brand</option>
                    {MAKER_TERMS_LINKS.map((m) => (
                      <option key={m.brand} value={m.brand}>
                        {m.brand}
                      </option>
                    ))}
                    <option value={ANOTHER_BRAND}>{ANOTHER_BRAND}</option>
                  </select>
                  {maker ? (
                    <div className="text-[13px] text-gray-700 mt-2 leading-[1.5]">
                      Need the lengths?{" "}
                      <a
                        href={maker.href}
                        data-maker-terms={maker.brand}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#0469f0] hover:underline font-semibold inline-flex items-center gap-1"
                      >
                        {maker.label}
                        <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
                      </a>
                    </div>
                  ) : brand === ANOTHER_BRAND ? (
                    <div className="text-[13px] text-gray-700 mt-2 leading-[1.5]">{ANOTHER_BRAND_INSTRUCTION}</div>
                  ) : null}
                </div>

                <div>
                  <label
                    htmlFor="warranty-exp-date"
                    className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2"
                  >
                    Purchase date
                  </label>
                  <input
                    id="warranty-exp-date"
                    type="date"
                    value={purchaseDate}
                    onChange={(e) => setPurchaseDate(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  />
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Use the date on your receipt or registration
                  </div>
                </div>

                <fieldset className="border-0 p-0 m-0">
                  <legend className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-1">
                    Coverage lengths, in years — enter from your warranty
                  </legend>
                  <p className="text-[11px] text-gray-500 mb-3 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5 flex-shrink-0" /> Read each length from the warranty statement for your model
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {lengthInputs.map((f) => (
                      <div key={f.key} className="min-w-0">
                        <label htmlFor={`warranty-exp-${f.key}`} className="block">
                          <span id={`warranty-exp-${f.key}-label`} className="block text-[11px] font-semibold text-gray-700 mb-1.5">{f.label}</span>
                          <input
                            id={`warranty-exp-${f.key}`}
                            aria-labelledby={`warranty-exp-${f.key}-label`}
                            type="number"
                            inputMode="decimal"
                            min={0}
                            max={50}
                            step={0.5}
                            value={f.value}
                            placeholder={f.placeholder}
                            onChange={(e) => f.set(e.target.value)}
                            className="tool-input-clean w-full px-3 py-3 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white placeholder:text-[12px] placeholder:font-normal placeholder:text-gray-500"
                          />
                        </label>
                      </div>
                    ))}
                  </div>
                </fieldset>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Your expiration dates
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>

                  {!result && (
                    <div className="text-[15px] text-white/80">
                      Enter your purchase date and at least one coverage length from your warranty.
                    </div>
                  )}

                  {result && (
                    <div className="space-y-3">
                      {LAYERS.map((row) => {
                        const data = result[row.key]
                        return (
                          <div
                            key={row.key}
                            className="rounded-lg p-4 bg-white/10 border border-white/15"
                          >
                            <div className="flex items-baseline justify-between gap-3 mb-1">
                              <div className="text-[11px] uppercase tracking-[0.15em] text-white/60 font-bold">
                                {row.label}
                              </div>
                              {data.entered && (
                                <div
                                  className="text-[10px] font-mono font-bold uppercase tracking-[0.1em]"
                                  style={{ color: data.status === "Active" ? "#86efac" : "#fca5a5" }}
                                >
                                  {data.status}
                                </div>
                              )}
                            </div>
                            {data.entered ? (
                              <>
                                <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                                  {data.date}
                                </div>
                                {data.status === "Active" && data.months > 0 && (
                                  <div className="text-[12px] text-white/70 mt-1">
                                    ~{data.months} months remaining
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="text-[13px] text-white/75">
                                Enter the length from your warranty to date this layer. {row.hint}.
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · If yours is out of warranty"
          title="Top-freezer refrigerators with fresh coverage"
          description="Lower-cost replacements from major brands with full manufacturer warranties."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Date math against <em className="italic text-[#ff9951]">your</em> terms.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Nothing is pre-filled. Warranty terms are set by each maker and change by model and
                model year, so you enter each length from the statement for your model. The calculator
                adds it to your purchase date and shows whether that layer is still active.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Standard parts + labor",
                  d: "Expiration = purchase date + the parts-and-labor length in your statement.",
                },
                {
                  n: "02",
                  t: "Compressor",
                  d: "If your statement covers the compressor longer than the standard term, enter that length. Check whether it is parts-only.",
                },
                {
                  n: "03",
                  t: "Sealed system",
                  d: "If your statement covers the sealed system longer, enter that length. It covers the compressor, evaporator, condenser, and connecting tubing.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Bottom-freezer refrigerators"
          description="Strong reliability records and accessible service points if anything fails."
          collectionType="bottom-freezer"
          fallbackCollectionType="top-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
